import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";

export interface PicklistValue {
  id: string;
  org_id: string | null;
  category: string;
  value: string;
  label: string;
  bar_label: string | null;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * Fetch active picklist values for a given category.
 * System defaults (org_id = NULL) are merged with org-specific overrides;
 * the org row wins when both exist for the same value key.
 */
export function usePicklistValues(category: string) {
  const { data: org } = useOrganization();

  return useQuery({
    queryKey: ["picklist", category, org?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("picklist_value" as any)
        .select("*")
        .eq("category", category)
        .or(`org_id.is.null,org_id.eq.${org!.id}`)
        .eq("is_active", true)
        .order("sort_order");

      if (error) throw error;

      // Org-specific rows override system defaults for the same value key
      const seen = new Map<string, PicklistValue>();
      for (const row of data as PicklistValue[]) {
        if (!seen.has(row.value) || row.org_id !== null) {
          seen.set(row.value, row);
        }
      }
      return Array.from(seen.values());
    },
    enabled: !!org?.id,
    staleTime: 5 * 60_000,
  });
}

/**
 * Fetch ALL picklist values (including inactive) for a category.
 * Used by the Settings → Picklists admin UI.
 */
export function usePicklistValuesAdmin(category: string) {
  const { data: org } = useOrganization();

  return useQuery({
    queryKey: ["picklist-admin", category, org?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("picklist_value" as any)
        .select("*")
        .eq("category", category)
        .or(`org_id.is.null,org_id.eq.${org!.id}`)
        .order("sort_order");

      if (error) throw error;

      // Org-specific rows override system defaults for the same value key
      const seen = new Map<string, PicklistValue>();
      for (const row of data as PicklistValue[]) {
        if (!seen.has(row.value) || row.org_id !== null) {
          seen.set(row.value, row);
        }
      }
      return Array.from(seen.values());
    },
    enabled: !!org?.id,
  });
}

/**
 * Toggle is_active on a picklist value. Admin only.
 */
export function useTogglePicklistValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("picklist_value" as any)
        .update({ is_active })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["picklist"] });
      queryClient.invalidateQueries({ queryKey: ["picklist-admin"] });
    },
  });
}
